/**
 * Ring 2 Effectiveness Hook (Phase 25.6)
 *
 * TanStack Query hook for the admin-only Ring 2 effectiveness analytics page.
 * Wraps GET /api/analytics/ring-2-effectiveness.
 */

import { useQuery } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';

// ============================================
// QUERY KEYS
// ============================================

export const ring2EffectivenessKeys = {
  all: ['ring-2-effectiveness'] as const,
  dashboard: (params: Ring2EffectivenessParams) =>
    [...ring2EffectivenessKeys.all, 'dashboard', params] as const,
};

// ============================================
// TYPES
// ============================================

export interface Ring2EffectivenessParams {
  startDate?: string; // ISO string
  endDate?: string; // ISO string
  sectionId?: string;
  shiftId?: string;
}

export interface Ring2TimeSeriesPoint {
  date: string; // YYYY-MM-DD
  refresherAssigned: number;
  refresherCompleted: number;
  focusedDrillAssigned: number;
  focusedDrillCompleted: number;
}

export interface Ring2SectionRow {
  sectionId: string;
  sectionTitle: string | null;
  sopTitle: string | null;
  assignedCount: number;
  completedCount: number;
  completionRate: number;
  isCanary: boolean;
}

export interface Ring2ShiftRow {
  shiftId: string;
  shiftName: string | null;
  assignedCount: number;
  completedCount: number;
  completionRate: number;
  isCanary: boolean;
}

export interface Ring2SkipReasonRow {
  reason: string;
  count: number;
}

export interface Ring2EffectivenessResponse {
  dateRange: {
    startDate: string;
    endDate: string;
  };
  filters: {
    sectionId: string | null;
    shiftId: string | null;
  };
  summary: {
    totalAssigned: number;
    totalCompleted: number;
    overallCompletionRate: number | null;
    canarySectionCount: number;
    canaryShiftCount: number;
  };
  series: Ring2TimeSeriesPoint[];
  sectionBreakdown: Ring2SectionRow[];
  shiftBreakdown: Ring2ShiftRow[];
  skippedByReason: Ring2SkipReasonRow[];
  canary: {
    completionThreshold: number;
    minAssignments: number;
  };
}

// ============================================
// HOOK
// ============================================

function buildSearchParams(params: Ring2EffectivenessParams): string {
  const qs = new URLSearchParams();
  if (params.startDate) qs.set('startDate', params.startDate);
  if (params.endDate) qs.set('endDate', params.endDate);
  if (params.sectionId) qs.set('sectionId', params.sectionId);
  if (params.shiftId) qs.set('shiftId', params.shiftId);
  return qs.toString();
}

export function useRing2Effectiveness(params: Ring2EffectivenessParams) {
  const api = useApi();

  return useQuery({
    queryKey: ring2EffectivenessKeys.dashboard(params),
    queryFn: async () => {
      const qs = buildSearchParams(params);
      const url = qs
        ? `/api/analytics/ring-2-effectiveness?${qs}`
        : '/api/analytics/ring-2-effectiveness';
      return api.get<Ring2EffectivenessResponse>(url);
    },
    staleTime: 5 * 60 * 1000,
  });
}
